/**
 * Fathom Analytics Event Tracking Configuration
 *
 * Event Naming Convention: page_section_element_action
 *
 * - page: The page or main section (e.g., 'scans', 'pricing', 'trial')
 * - section: The specific section within the page (e.g., 'hero', 'how-to', 'features')
 * - element: The UI element being interacted with (e.g., 'button', 'link', 'icon')
 * - action: The action being performed (e.g., 'click', 'copy', 'submit')
 *
 * Example: 'scans_how-to_gradle_icon_click'
 *
 * To add a new event:
 * 1. Add the event to the appropriate page object below
 * 2. Use the typed event name in your component with useFathomEvent hook
 * 3. Follow the naming convention strictly for consistency
 */

type FathomEventStructure = {
  [page: string]: {
    [section: string]: {
      [element: string]: {
        [action: string]: string;
      };
    };
  };
};

export const FATHOM_EVENTS = {
  scans: {
    'how-to': {
      gradle_icon: {
        click: 'scans_how-to_gradle_icon_click',
      },
      maven_icon: {
        click: 'scans_how-to_maven_icon_click',
      },
      sbt_icon: {
        click: 'scans_how-to_sbt_icon_click',
      },
      instructions: {
        copy: 'scans_how-to_instructions_copy',
        click: 'scans_how-to_instructions_click',
      },
      quickstart_link: {
        click: 'scans_how-to_quickstart_link_click',
      },
    },
  },
} as const satisfies FathomEventStructure;

// Legacy events - these should be migrated to the structured format above and not added to.
export const LEGACY_FATHOM_EVENTS = {
  TRIAL_REQUEST: 'trial-request',
  PRICING_REQUEST: 'pricing-request',
  NEWSLETTER_FORM: 'newsletter-form',
  MARKETING_BANNER_LINK_CLICK: 'marketing-banner-link-click',
} as const;

// Type helper to extract event names
type ExtractEventNames<T> = T extends string
  ? T
  : T extends Record<string, unknown>
    ? { [K in keyof T]: ExtractEventNames<T[K]> }[keyof T]
    : never;

// Export types for event names
export type FathomEventName = ExtractEventNames<typeof FATHOM_EVENTS>;
export type LegacyFathomEventName = (typeof LEGACY_FATHOM_EVENTS)[keyof typeof LEGACY_FATHOM_EVENTS];
export type AllFathomEventNames = FathomEventName | LegacyFathomEventName;
